import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2, Lock, Globe } from "lucide-react";
import { toast } from "sonner";
import { useAllShows } from "../hooks/useShows";
import Poster from "../components/Poster";
import { TOAST_MS } from "../lib/constants";
import {
  getList, patchList, deleteList,
  addToList, removeFromList,
} from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

export default function ListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const allShows = useAllShows();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    try {
      const d = await getList(id);
      setData(d);
      setError(null);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const showsInList = data?.shows || [];
  const showIdsInList = useMemo(
    () => new Set(showsInList.map((x) => x.show.id)),
    [showsInList]
  );

  const candidates = useMemo(() => {
    const q = search.toLowerCase();
    return allShows
      .filter((s) => !showIdsInList.has(s.id))
      .filter(
        (s) =>
          !q ||
          lower(s.title).includes(q) ||
          lower(s.theatre).includes(q)
      );
  }, [allShows, showIdsInList, search]);

  const updateField = async (patch) => {
    try {
      await patchList(id, patch);
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const handleAdd = async (showId) => {
    try {
      await addToList(id, showId);
      toast("added", { duration: TOAST_MS.FAST });
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const handleRemove = async (showId) => {
    try {
      await removeFromList(id, showId);
      toast("removed", { duration: TOAST_MS.FAST });
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("delete this list?")) return;
    try {
      await deleteList(id);
      toast("deleted", { duration: TOAST_MS.FAST });
      navigate("/lists");
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  if (error) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-display text-3xl">list not found.</div>
        <p className="font-mono text-xs text-white/50 mt-2">{error}</p>
        <Link to="/lists" className="mt-4 inline-block font-mono text-sm text-acid underline">
          back to lists
        </Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-mono text-xs text-white/40">loading...</div>
      </main>
    );
  }

  return (
    <main data-testid={`list-detail-${data.id}`} className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <button
          onClick={() => navigate("/lists")}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> lists
        </button>

        <input
          data-testid="list-title-input"
          value={data.title}
          onChange={(e) => setData({ ...data, title: e.target.value })}
          onBlur={(e) => updateField({ title: e.target.value })}
          placeholder="untitled list"
          className="mt-3 w-full bg-transparent font-display text-4xl sm:text-5xl leading-none focus:outline-none placeholder:text-white/20"
        />
        <textarea
          data-testid="list-description-input"
          value={data.description || ""}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          onBlur={(e) => updateField({ description: e.target.value })}
          placeholder="optional description"
          rows={2}
          className="mt-3 w-full bg-transparent font-mono text-xs text-white/70 focus:outline-none placeholder:text-white/30 resize-none"
        />
        <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-white/40">
          <span>{data.itemCount} {data.itemCount === 1 ? "show" : "shows"}</span>
          <span>·</span>
          <button
            data-testid="list-toggle-visibility"
            onClick={() =>
              updateField({ visibility: data.visibility === "public" ? "private" : "public" })
            }
            className="inline-flex items-center gap-1 hover:text-acid transition-colors"
          >
            {data.visibility === "public" ? (
              <><Globe className="w-3 h-3" /> public</>
            ) : (
              <><Lock className="w-3 h-3" /> private</>
            )}
          </button>
        </div>
      </header>

      <section className="px-5 py-5">
        <button
          data-testid="list-add-show-btn"
          onClick={() => setShowPicker((v) => !v)}
          className="w-full font-mono text-xs lowercase border-2 border-acid text-acid py-3 hover:bg-acid hover:text-black transition-colors flex items-center justify-center gap-1.5"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          {showPicker ? "close" : "add show"}
        </button>

        {showPicker && (
          <div data-testid="list-show-picker" className="mt-4 border border-white/15">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="search"
              className="w-full bg-transparent border-b border-white/15 px-3 py-3 font-mono text-sm placeholder:text-white/30 focus:outline-none"
            />
            <ul className="max-h-[40vh] overflow-y-auto">
              {candidates.length === 0 ? (
                <li className="px-3 py-4 font-mono text-xs text-white/40 text-center">
                  no matches
                </li>
              ) : (
                candidates.map((s) => (
                  <li key={s.id}>
                    <button
                      data-testid={`list-add-${s.id}`}
                      onClick={() => handleAdd(s.id)}
                      className="w-full text-left flex items-center gap-3 px-3 py-2 border-b border-white/10 hover:bg-acid/10 transition-colors"
                    >
                      <Poster show={s} size="sm" />
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-sm truncate">{lower(s.title)}</div>
                        <div className="font-mono text-[10px] text-white/50 truncate">
                          {lower(s.theatre)}
                        </div>
                      </div>
                      <Plus className="w-4 h-4 text-acid shrink-0" strokeWidth={2} />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </section>

      <section data-testid="list-shows" className="border-t-2 border-white/15">
        {showsInList.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="font-mono text-xs text-white/40">no shows in this list yet.</p>
          </div>
        ) : (
          <ul>
            {showsInList.map((item) => {
              const s = item.show;
              return (
                <li
                  key={s.id}
                  data-testid={`list-show-${s.id}`}
                  className="border-b border-white/10 px-5 py-4 flex items-center gap-4"
                >
                  <Link to={`/show/${s.id}`} className="shrink-0">
                    <Poster show={s} size="sm" />
                  </Link>
                  <Link to={`/show/${s.id}`} className="flex-1 min-w-0">
                    <div className="font-display text-base leading-tight truncate">
                      {lower(s.title)}
                    </div>
                    <div className="font-mono text-[10px] text-white/50 truncate">
                      {lower(s.theatre)} · {lower(s.dateLabel)}
                    </div>
                  </Link>
                  <button
                    data-testid={`list-remove-${s.id}`}
                    onClick={() => handleRemove(s.id)}
                    className="shrink-0 p-1.5 text-white/40 hover:text-acid"
                    aria-label="remove from list"
                  >
                    <X className="w-4 h-4" strokeWidth={2} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="px-5 py-8">
        <button
          data-testid="list-delete-btn"
          onClick={handleDelete}
          className="font-mono text-[11px] lowercase border-2 border-white/30 text-white/60 px-3 py-2 hover:border-white hover:text-white transition-colors flex items-center gap-1.5"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
          delete list
        </button>
      </section>
    </main>
  );
}
